/**
 * Recuperação de chunk órfão.
 *
 * O servidor não guarda resquício: cada deploy do Firebase Hosting é um release
 * completo, e arquivo que não está no release novo simplesmente não é servido. O
 * resquício vive no CLIENTE — numa aba (ou WebView) que já carregou um index.html
 * antigo e continua aberta enquanto um deploy troca os chunks debaixo dela.
 *
 * Quando essa aba navega para uma rota lazy, ela pede um `chunk-<hash antigo>.js`
 * que não existe mais. E aí o rewrite `** → /index.html` do firebase.json responde
 * o index.html com HTTP 200 e Content-Type text/html: o navegador recebe HTML onde
 * esperava um módulo JS, o import rejeita e a navegação morre em silêncio. Do lado
 * do usuário é "o app parou de abrir essa tela".
 *
 * Como o index.html é servido com no-store, um reload resolve por definição: traz o
 * index novo, que aponta para os hashes novos.
 *
 * A trava contra loop é a GERAÇÃO do bundle, não um contador de tentativas. Cada
 * index.html carrega um `main-<hash>.js` diferente, então o nome desse arquivo
 * identifica de qual deploy é a página em execução:
 *
 *   - falhou numa geração ainda não tentada → recarrega (é o caso do deploy novo);
 *   - falhou de novo NA MESMA geração → já estamos na versão mais recente que o
 *     servidor tem, recarregar não muda nada. Deixa o erro subir.
 *
 * Um contador simples zerado no boot faria loop justamente no cenário ruim (deploy
 * publicado quebrado): recarrega, sobe, o usuário navega, quebra, recarrega...
 */

/**
 * As mensagens variam por engine: Chrome/WebView falam em "dynamically imported
 * module", Safari em "Importing a module script failed", e o formato ChunkLoadError
 * ainda aparece em builds com webpack.
 */
const CHUNK_ERROR =
  /ChunkLoadError|Loading chunk \S+ failed|Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed|expected a javascript(-or-wasm)? module script/i;

const TRIED_KEY = "orbitta:chunk-reload-generation";

/** Nome hasheado do entry point = identidade do deploy que está rodando nesta aba. */
function generation(): string {
  const script = document.querySelector<HTMLScriptElement>('script[src*="main-"]');
  const src = script?.getAttribute("src") || "";
  return src.split("/").pop() || "desconhecida";
}

function alreadyTried(current: string): boolean {
  try {
    return window.sessionStorage.getItem(TRIED_KEY) === current;
  } catch {
    // sessionStorage bloqueado (modo privado / WebView sem storage): sem trava
    // confiável, é mais seguro não recarregar do que arriscar loop.
    return true;
  }
}

function markTried(current: string): void {
  try {
    window.sessionStorage.setItem(TRIED_KEY, current);
  } catch {
    // Sem storage não há como marcar; alreadyTried() já barrou este caminho.
  }
}

function describe(message: unknown, reason: unknown): string {
  const error = reason as { message?: unknown; name?: unknown } | null | undefined;
  return [message, error?.message, error?.name]
    .filter((part) => typeof part === "string" && part)
    .join(" ");
}

function handle(message: unknown, reason: unknown): void {
  const text = describe(message, reason);
  if (!CHUNK_ERROR.test(text)) {
    return;
  }

  // Offline não é chunk órfão — recarregar só troca a tela por uma pior.
  if (navigator.onLine === false) {
    console.warn("[ChunkRecovery] Falha de chunk com o dispositivo offline; sem reload:", text);
    return;
  }

  const current = generation();

  if (alreadyTried(current)) {
    console.error(
      `[ChunkRecovery] Falha de chunk na geração ${current}, que já foi recarregada. ` +
        `O deploy publicado é que está inconsistente; deixando o erro subir:`,
      text
    );
    return;
  }

  console.warn(
    `[ChunkRecovery] Chunk órfão na geração ${current} (deploy novo com a aba aberta). Recarregando:`,
    text
  );
  markTried(current);
  window.location.reload();
}

/**
 * Chamar o mais cedo possível no main.ts: o boot em si não depende de chunk lazy,
 * mas a primeira navegação pode depender.
 */
export function installChunkRecovery(): void {
  if (typeof window === "undefined") {
    return;
  }

  window.addEventListener("error", (event) => handle(event.message, event.error));
  window.addEventListener("unhandledrejection", (event) => handle(undefined, event.reason));
}
